import { useEffect, useRef } from 'react'
import {
  Autocomplete,
  Button,
  FormControl,
  Grid,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import FsContainer from '../common/Container'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  getFileCharacteristics,
  setEtlFiles,
  setFileCharacteristics,
  setLatestEtlStatus,
  updateFileCharacteristics,
} from '../../../store/actions'
import { useLoading } from '../../../shared/hooks'
import ACTIONS from '../../../store/constants'
import { EtlFileCharacteristic } from '../../../shared/model/models'
import FsAlert, { FsAlertRef } from '../../common/atoms/Alert'

const Row = ({ characteristic }: { characteristic: EtlFileCharacteristic }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { etlFiles, etlFileCharacteristics } = useAppSelector((state) => state.currentEtl)

  if (etlFileCharacteristics && etlFiles) {
    const etlFileNames = etlFiles ? Object.keys(etlFiles) : []

    return (
      <Grid
        container
        p={1}
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        sx={{
          backgroundColor: theme.palette.action.disabledBackground,
          borderRadius: 2,
        }}
      >
        <Grid size={{sm: 5}}>
          <Stack flexDirection="row" gap={0.5} alignItems="center">
            <Typography>{characteristic.name}</Typography>
          </Stack>
        </Grid>
        <Grid size={{sm: 3}}>
          <FormControl
            fullWidth
            size="small"
            sx={{ backgroundColor: theme.palette.background.paper }}
          >
            <Autocomplete
              size="small"
              options={etlFileNames}
              value={characteristic.userFileName}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('select.file')}
                  required
                  error={characteristic.userFileName === ''}
                />
              )}
              onChange={(_, newValue: string | null) => {
                dispatch(updateFileCharacteristics(newValue ?? '', characteristic.id, 'file'))
              }}
              disabled={etlFileNames.length === 1}
            />
          </FormControl>
        </Grid>
        <Grid size={{sm: 3}}>
          <FormControl
            fullWidth
            size="small"
            sx={{ backgroundColor: theme.palette.background.paper }}
          >
            <Autocomplete
              size="small"
              options={
                etlFiles &&
                characteristic.userFileName &&
                etlFiles[characteristic.userFileName as string]
                  ? etlFiles[characteristic.userFileName as string]
                  : []
              }
              freeSolo
              value={characteristic.userColumnName}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('select.column')}
                  required
                  error={characteristic.userColumnName === ''}
                />
              )}
              onChange={(_, newValue: string | null) => {
                dispatch(updateFileCharacteristics(newValue ?? '', characteristic.id, 'column'))
              }}
              disabled={!characteristic.userFileName}
            />
          </FormControl>
        </Grid>
      </Grid>
    )
  }
  return null
}

const FsColumnsMatching = ({ setIsLoading }: { setIsLoading: (isLoading: boolean) => void }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const alertRef = useRef<FsAlertRef>(null)

  const { latestEtlStatus, etlFileCharacteristics, etlFiles } = useAppSelector(
    (state) => state.currentEtl,
  )

  useEffect(() => {
    if (latestEtlStatus) {
      dispatch(setEtlFiles({ runId: latestEtlStatus.id }))
    }
  }, [latestEtlStatus])

  useEffect(() => {
    if (!etlFileCharacteristics && etlFiles) {
      dispatch(getFileCharacteristics())
    }
  }, [etlFileCharacteristics, etlFiles])

  const { isFetching } = useLoading([
    ACTIONS.ETL.GET_FILE_CHARACTERISTICS,
    ACTIONS.ETL.SET_ETL_FILES,
    ACTIONS.ETL.SET_FILE_CHARACTERISTICS,
  ])

  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching])

  const isCompleted = !etlFileCharacteristics?.find(
    (char) => char.isShown && (!char.userFileName || !char.userColumnName),
  )

  const handleAccept = async (shouldGoNext: boolean) => {
    try {
      if (!shouldGoNext) {
        await dispatch(
          setFileCharacteristics({
            etlFileCharacteristic: etlFileCharacteristics,
            isCompleted: false,
          }),
        )
        alertRef.current?.openAlert({
          severity: 'success',
          message: t('column.matchings.saved'),
        })
      } else if (shouldGoNext) {
        if (isCompleted) {
          await dispatch(
            setFileCharacteristics({ etlFileCharacteristic: etlFileCharacteristics, isCompleted }),
          )
          dispatch(setLatestEtlStatus())
        } else {
          alertRef.current?.openAlert({
            severity: 'error',
            message: t('something.went.wrong'),
          })
        }
      }
    } catch (error) {
      alertRef.current?.openAlert({
        severity: 'error',
        message: t('something.went.wrong'),
      })
    }
  }

  return (
    <FsContainer
      footer={
        <Stack width="100%" direction="row" justifyContent="flex-end">
          <Stack direction="row" gap={1}>
            <Button onClick={() => handleAccept(false)} variant="outlined">
              {t('save')}
            </Button>
            <Button onClick={() => handleAccept(true)} disabled={!isCompleted} variant="contained">
              {t('save.continue')}
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Stack alignItems="flex-start" gap={1}>
        <Typography mt={1} ml={0.25}>
          {t('column.matching.description')}
        </Typography>
        <Grid container py={1} alignItems="center" justifyContent="space-between" width="100%">
          <Grid size={{sm: 5}}>
            <Typography variant="h6">{t('characteristic')}</Typography>
          </Grid>
          <Grid size={{sm: 3}}>
            <Typography variant="h6">{t('file')}</Typography>
          </Grid>
          <Grid size={{sm: 3}}>
            <Typography variant="h6">{t('column')}</Typography>
          </Grid>
        </Grid>
        {etlFileCharacteristics
          ?.filter((char) => char.isShown)
          .map((char, index: number) => (
            <Row characteristic={char} key={index} />
          ))}
      </Stack>
      <FsAlert ref={alertRef} />
    </FsContainer>
  )
}

export default FsColumnsMatching
